import { ref, computed } from 'vue';
import { getBluegroundPromoCopy } from './bluegroundAffiliate';
import { getSpacestPromoCopy, SPACEST_SHOWCASE_PATH } from './spacestAffiliate';

/** Ruta al escaparate Spacest (todas las habitaciones en Coolliving). */
export const spacestShowcasePath = SPACEST_SHOWCASE_PATH;
import {
  getLeadSessionProfile,
  saveLeadSessionProfile,
} from '@/services/scrapedLeadSubmit';
import { postOutboundAffiliateNotify } from './affiliateOutboundNotify';
import { resolveSpacestListingMonthlyPriceEur } from './spacestListingPrice';

export const isAffiliatePartnerModalVisible = ref(false);
export const affiliatePartnerColiving = ref(null);
/** @type {import('vue').Ref<'outsite'|'blueground'|'spacest'|'aya'|null>} */
export const affiliatePartnerKind = ref(null);

const redirectOptions = ref({});
/** Email capturado en el modal (consentimiento) — se envía en /api/out/notify */
export const affiliateReferrerEmail = ref('');

let copyNotifiedThisSession = false;

export const bluegroundPromoCopy = computed(() => {
  if (!affiliatePartnerColiving.value || affiliatePartnerKind.value !== 'blueground') return null;
  return getBluegroundPromoCopy(affiliatePartnerColiving.value);
});

export const spacestPromoCopy = computed(() => {
  if (affiliatePartnerKind.value !== 'spacest') return null;
  return getSpacestPromoCopy();
});

export async function openAffiliatePartnerModal(coliving, options = {}) {
  if (!coliving) return;
  const {
    isBluegroundAffiliateListing,
    isOutsiteAffiliateListing,
    isSpacestPartnerListing,
    isAyaPartnerListing,
  } = await import('./externalRedirect');
  if (isBluegroundAffiliateListing(coliving)) {
    affiliatePartnerKind.value = 'blueground';
  } else if (isOutsiteAffiliateListing(coliving)) {
    affiliatePartnerKind.value = 'outsite';
  } else if (isSpacestPartnerListing(coliving)) {
    affiliatePartnerKind.value = 'spacest';
  } else if (isAyaPartnerListing(coliving)) {
    affiliatePartnerKind.value = 'aya';
  } else {
    return;
  }
  affiliatePartnerColiving.value = coliving;
  redirectOptions.value = { ...options };
  copyNotifiedThisSession = false;

  const session = getLeadSessionProfile();
  if (session?.applicantEmail && session.consentLeadSharing) {
    affiliateReferrerEmail.value = String(session.applicantEmail).trim().toLowerCase();
    // Outsite: redirect directo; Blueground + Spacest: modal con promo code
    if (affiliatePartnerKind.value === 'outsite') {
      await confirmAffiliatePartnerRedirect();
      return;
    }
    isAffiliatePartnerModalVisible.value = true;
    return;
  }

  affiliateReferrerEmail.value = '';
  isAffiliatePartnerModalVisible.value = true;
}

export function closeAffiliatePartnerModal() {
  isAffiliatePartnerModalVisible.value = false;
  affiliatePartnerColiving.value = null;
  affiliatePartnerKind.value = null;
  redirectOptions.value = {};
  affiliateReferrerEmail.value = '';
}

function persistAffiliateReferrerEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  if (!normalized) return;
  saveLeadSessionProfile({
    ...(getLeadSessionProfile() || {}),
    applicantEmail: normalized,
    consentLeadSharing: true,
  });
}

function buildNotifyOptions(extra = {}) {
  const email = affiliateReferrerEmail.value?.trim().toLowerCase() || '';
  const { userId, source = 'coliving-search' } = redirectOptions.value;
  return {
    userId,
    source,
    referrerEmail: email || undefined,
    ...extra,
  };
}

/** Continuar tras email + consentimiento → redirect afiliado */
export async function confirmAffiliatePartnerRedirect() {
  const coliving = affiliatePartnerColiving.value;
  if (!coliving) return;

  // Capturar antes de closeAffiliatePartnerModal (resetea refs compartidos)
  const options = buildNotifyOptions();
  if (options.referrerEmail) {
    persistAffiliateReferrerEmail(options.referrerEmail);
  }
  closeAffiliatePartnerModal();
  const { openExternalColivingListing } = await import('./externalRedirect');
  openExternalColivingListing(coliving, options);
}

/** Copia código promo (Blueground / Spacest) — notifica admin */
export async function notifyAffiliatePromoCopyOnce() {
  if (copyNotifiedThisSession) return;
  const kind = affiliatePartnerKind.value;
  if (kind !== 'blueground' && kind !== 'spacest') return;

  const coliving = affiliatePartnerColiving.value;
  if (!coliving) return;

  const email = affiliateReferrerEmail.value?.trim();
  if (!email) return;

  const promo = kind === 'spacest'
    ? getSpacestPromoCopy()
    : getBluegroundPromoCopy(coliving);
  const { resolveExternalApplyUrl, resolveExternalProvider } = await import('./externalRedirect');
  const url = resolveExternalApplyUrl(coliving) || '';
  const notifyOpts = buildNotifyOptions();

  copyNotifiedThisSession = true;
  postOutboundAffiliateNotify({
    url,
    provider: resolveExternalProvider(coliving),
    colivingId: coliving?.id || '',
    colivingTitle: coliving?.title || coliving?.propertyTitle || '',
    userId: notifyOpts.userId || '',
    city: coliving?.propertyLocation || coliving?.city || '',
    source: notifyOpts.source || 'coliving-search',
    action: 'copy-promo-code',
    promoCode: promo?.code || '',
    referrerEmail: email,
    priceFrom: resolveSpacestListingMonthlyPriceEur(coliving) ?? '',
  });
}
